import { apiRequest } from './client'

/**
 * Who tax invoices are made out to (null until an owner fills them in). With a GSTIN or a PAN, they are also what
 * business verification checks.
 */
export type BillingDetails = {
  legalName: string | null
  gstin: string | null
  pan: string | null
  billingAddress: string | null
  stateCode: string | null
  billingEmail: string | null
}

/** The org's prepaid account (apps/platform/src/common/responses.ts billingAccountResponse). Money in paise. */
export type BillingAccount = {
  balancePaise: number
  ratePaisePerMinute: number
  isCustomRate: boolean
  lowBalancePaise: number
  /** The credit that expires next (credit lasts 365 days from when it was added), or null. */
  nextExpiry: { paise: number; expiresAt: string } | null
  details: BillingDetails
}

export type TopUp = {
  id: string
  amountPaise: number
  gstPaise: number
  totalPaise: number
  status: 'created' | 'paid' | 'expired'
  /** Where to pay, while the top-up is open. */
  paymentUrl: string | null
  createdAt: string
  paidAt: string | null
}

export type GstState = { code: string; name: string }

export type CreditEntry = {
  id: string
  kind: 'top_up' | 'call' | 'grant' | 'adjustment' | 'expiry' | 'tds'
  amountPaise: number
  balanceAfterPaise: number
  callId: string | null
  note: string | null
  createdAt: string
}

export type ChargedUsage = {
  days: number
  daily: { day: string; calls: number; seconds: number; chargedPaise: number }[]
  byFlow: { flowId: string | null; flowName: string | null; calls: number; seconds: number; chargedPaise: number }[]
}

type InvoiceParty = {
  legalName: string
  gstin: string | null
  address: string
  stateCode: string
  stateName: string
  /** The seller's CIN and contacts; absent on buyers and older invoices (phone until RowCall sets one). */
  cin?: string
  email?: string
  website?: string
  phone?: string
}

export type Invoice = {
  id: string
  number: string
  issuedAt: string
  seller: InvoiceParty
  buyer: InvoiceParty
  sac: string
  taxablePaise: number
  cgstPaise: number
  sgstPaise: number
  igstPaise: number
  totalPaise: number
}

/** TDS the org paid to the government on a top-up (at most 2% before GST), credited to its balance from the certificate. */
export type TdsCredit = {
  id: string
  topUpId: string | null
  amountPaise: number
  certificateNumber: string
  /** '2026-27' */
  financialYear: string
  quarter: 1 | 2 | 3 | 4
  creditedAt: string
}

export const fetchBilling = (orgId: string) =>
  apiRequest<{ account: BillingAccount; topUps: TopUp[]; states: GstState[] }>('GET', `/api/orgs/${orgId}/billing`)

export const fetchUsage = (orgId: string, days = 30) =>
  apiRequest<ChargedUsage>('GET', `/api/orgs/${orgId}/billing/usage?days=${days}`)

export const fetchLedger = (orgId: string, cursor?: string) =>
  apiRequest<{ entries: CreditEntry[]; nextCursor: string | null }>(
    'GET',
    `/api/orgs/${orgId}/billing/ledger${cursor ? `?cursor=${encodeURIComponent(cursor)}` : ''}`,
  )

export const saveBillingDetails = (orgId: string, details: Omit<BillingDetails, 'stateCode'> & { stateCode: string }) =>
  apiRequest<{ account: BillingAccount }>('PUT', `/api/orgs/${orgId}/billing/details`, details)

export const startTopUp = (orgId: string, amountPaise: number) =>
  apiRequest<{ topUp: TopUp }>('POST', `/api/orgs/${orgId}/top-ups`, { amountPaise })

export const setLowBalanceAlert = (orgId: string, lowBalancePaise: number) =>
  apiRequest<{ account: BillingAccount }>('PUT', `/api/orgs/${orgId}/billing/alert`, { lowBalancePaise })

// ─── Staff ───

export const fetchOrgBillingAsStaff = (orgId: string) =>
  apiRequest<{
    account: BillingAccount
    entries: CreditEntry[]
    /** Paid top-ups, newest first, to credit TDS against. */
    topUps: TopUp[]
    tdsCredits: TdsCredit[]
  }>('GET', `/api/admin/orgs/${orgId}/billing`)

export const creditTdsAsStaff = (
  orgId: string,
  input: Pick<TdsCredit, 'topUpId' | 'amountPaise' | 'certificateNumber' | 'financialYear' | 'quarter'>,
) => apiRequest<{ tdsCredit: TdsCredit }>('POST', `/api/admin/orgs/${orgId}/tds`, input)

export const setOrgRateAsStaff = (orgId: string, ratePaisePerMinute: number | null) =>
  apiRequest<{ account: BillingAccount }>('PATCH', `/api/admin/orgs/${orgId}/billing`, { ratePaisePerMinute })

export const addCreditAsStaff = (
  orgId: string,
  input: { kind: 'grant' | 'adjustment'; amountPaise: number; note: string },
) => apiRequest<{ entry: CreditEntry }>('POST', `/api/admin/orgs/${orgId}/credit`, input)

export const fetchTdsCredits = (orgId: string) =>
  apiRequest<{ tdsCredits: TdsCredit[] }>('GET', `/api/orgs/${orgId}/billing/tds`)

export const fetchInvoices = (orgId: string) =>
  apiRequest<{ invoices: Invoice[] }>('GET', `/api/orgs/${orgId}/invoices`)

export const fetchInvoice = (orgId: string, invoiceId: string) =>
  apiRequest<{ invoice: Invoice }>('GET', `/api/orgs/${orgId}/invoices/${invoiceId}`)

/** Where the browser downloads the invoice as a PDF (a link, so it saves straight to disk). */
export const invoicePdfUrl = (orgId: string, invoiceId: string): string =>
  `/api/orgs/${orgId}/invoices/${invoiceId}/pdf`
